import { create } from 'zustand';
import type { Perfil, Topico, Subtopico, EcadAutomacaoPasso } from '../types';
import { api } from '../../../../api/client';

export function normalizeTipoAcao(tipo: string): string {
  if (!tipo) return 'EXPLICAR';
  const u = tipo.toUpperCase().trim();
  if (u === 'NAVIGATE') return 'ESPERAR_PAGINA';
  if (u === 'CLICK') return 'MOVER_E_CLICAR';
  if (u === 'TYPE' || u === 'FILL') return 'DIGITAR';
  if (u === 'WAIT') return 'ESPERAR_TEMPO';
  if (u === 'ASSERT' || u === 'SCREENSHOT' || u === 'HIGHLIGHT') return 'DESTACAR';
  return u;
}

export function sanitizeAcaoValor(tipoAcao: string, valor: string): string {
  if (!valor) return '';
  const normalizedTipo = normalizeTipoAcao(tipoAcao);
  if (normalizedTipo === 'ESPERAR_PAGINA') {
    let cleaned = valor.trim();
    if (cleaned.endsWith('.html')) {
      cleaned = cleaned.substring(0, cleaned.length - 5);
    }
    cleaned = cleaned.replace(/\.html/g, '');
    if (cleaned === 'login' || cleaned === '/login') return '/login';
    if (cleaned === 'admin' || cleaned === '/admin') return '/admin/dashboard';
    if (cleaned === 'ativar' || cleaned === '/ativar') return '/ativar';
    if (cleaned === 'conectando' || cleaned === '/conectando') return '/conectando';
    if (!cleaned.startsWith('http://') && !cleaned.startsWith('https://') && !cleaned.startsWith('/')) {
      cleaned = '/' + cleaned;
    }
    return cleaned;
  }
  return valor;
}

// Module-level singletons for non-serializable WebSocket and audio player
let activeSocket: WebSocket | null = null;
let activeAudio: HTMLAudioElement | null = null;
let streamCanvas: HTMLCanvasElement | null = null;

export interface EcadState {
  streamCanvas: HTMLCanvasElement | null;
  // Catalog selection
  selectedSubtopicoId: string | null;
  selectedRoteiro: Subtopico | null;
  selectedPerfilId: string | null;
  selectedTopicoId: string | null;
  passos: EcadAutomacaoPasso[];
  loadingRoteiro: boolean;
  savingPassos: boolean;

  // Modals
  showPerfilModal: boolean;
  showTopicoModal: boolean;
  showSubtopicoModal: boolean;
  showArchivedModal: boolean;
  customTopicOrder: string[];

  // Forms
  perfilForm: { id: string; nome: string; isInterno: boolean };
  topicoForm: { id: string; perfilId: string; titulo: string };
  subtopicoForm: {
    id: string;
    topicoId: string;
    titulo: string;
    descricao: string;
    duracao: string;
    dificuldade: string;
    videoUrl: string;
    pdfUrl: string;
  };
  roteiroEditForm: {
    titulo: string;
    descricao: string;
    videoUrl: string;
    pdfUrl: string;
    duracao: string;
    dificuldade: string;
    urlInicial: string;
    velocidadeDigitacaoMs: number;
    mouseSteps: number;
    vozTutor: string;
  };

  // State
  savingRoteiroMeta: boolean;
  isEditingMetadata: boolean;
  activeTab: 'editor' | 'midias' | 'simulacao' | 'restaurar';
  snapshots: any[];
  loadingSnapshots: boolean;
  restoringSnapshot: boolean;
  passosEditMode: 'visual' | 'json';
  jsonValue: string;
  generatingPdf: boolean;
  pdfProgressShow: boolean;
  pdfProgressStep: number;
  pdfProgressError: string | null;
  pdfProgressUrl: string | null;
  pdfLogs: string[];

  // Scanner
  showScannerModal: boolean;
  scannerForm: { url: string; pageKey: string };
  scanningPage: boolean;

  // Simulator
  isSimulating: boolean;
  simulationLogs: string[];
  simulationSuccess: boolean | null;
  simulationModeVisible: boolean;
  activeStepOrdem: number | null;
  currentBrowserUrl: string;
  isPaused: boolean;
  precacheInfo: { active: boolean; total: number; completed: number } | null;
  browserInstance: { active: boolean; headless: boolean | null; startTime: number | null } | null;
  toastMessage: string | null;
  toastType: 'success' | 'error';
  uiMap: Record<string, Record<string, { seletor: string; descricao: string }>>;

  // Dry-run
  isDryRunning: boolean;
  dryRunReport: any[] | null;
  showDryRunPanel: boolean;
  dryRunFullReport: any | null;
  retryingStep: number | null;
  openingStep: number | null;

  // Copilot
  copilotMessages: Array<{ role: 'user' | 'assistant'; text: string }>;
  copilotInput: string;
  copilotLoading: boolean;
  selectedStepIndexes: number[];
  proposedUiActions: any[] | null;
  activeEditingIndex: number | null;
  includePerfilContext: boolean;
  includeTopicoContext: boolean;
  includeSubtopicoContext: boolean;
  includePassosContext: boolean;
  copilotSessionHistory: any[];
  activeSessionId: string;

  // Setters & Actions
  setStreamCanvas: (canvas: HTMLCanvasElement | null) => void;
  setSelectedSubtopicoId: (id: string | null, perfis: Perfil[]) => void;
  setSelectedPerfilId: (id: string | null) => void;
  setSelectedTopicoId: (id: string | null) => void;
  setPassos: (passos: EcadAutomacaoPasso[]) => void;
  setJsonValue: (val: string) => void;
  setActiveTab: (tab: 'editor' | 'midias' | 'simulacao' | 'restaurar') => void;
  setSavingPassos: (val: boolean) => void;
  setIsEditingMetadata: (val: boolean) => void;
  setRoteiroEditForm: (form: any) => void;
  setPerfilForm: (form: any) => void;
  setTopicoForm: (form: any) => void;
  setSubtopicoForm: (form: any) => void;
  setShowPerfilModal: (val: boolean) => void;
  setShowTopicoModal: (val: boolean) => void;
  setShowSubtopicoModal: (val: boolean) => void;
  setShowArchivedModal: (val: boolean) => void;
  setCustomTopicOrder: (order: string[]) => void;
  setPassosEditMode: (mode: 'visual' | 'json') => void;
  setPdfProgressShow: (val: boolean) => void;
  setPdfProgressStep: (step: number) => void;
  setPdfProgressError: (err: string | null) => void;
  setPdfProgressUrl: (url: string | null) => void;
  setPdfLogs: (logs: string[]) => void;
  setShowScannerModal: (val: boolean) => void;
  setScannerForm: (form: any) => void;
  setScanningPage: (val: boolean) => void;
  setIsSimulating: (val: boolean) => void;
  setSimulationLogs: (logs: string[] | ((prev: string[]) => string[])) => void;
  setSimulationSuccess: (val: boolean | null) => void;
  setSimulationModeVisible: (val: boolean) => void;
  setActiveStepOrdem: (ordem: number | null) => void;
  setCurrentBrowserUrl: (url: string) => void;
  setIsPaused: (val: boolean) => void;
  setPrecacheInfo: (info: any) => void;
  setBrowserInstance: (inst: any) => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  clearToast: () => void;
  setUiMap: (map: any) => void;
  setIsDryRunning: (val: boolean) => void;
  setDryRunReport: (report: any[] | null) => void;
  setShowDryRunPanel: (val: boolean) => void;
  setDryRunFullReport: (report: any | null) => void;
  setRetryingStep: (step: number | null) => void;
  setOpeningStep: (step: number | null) => void;
  setCopilotMessages: (msgs: any[] | ((prev: any[]) => any[])) => void;
  setCopilotInput: (val: string) => void;
  setCopilotLoading: (val: boolean) => void;
  setSelectedStepIndexes: (indexes: number[] | ((prev: number[]) => number[])) => void;
  setProposedUiActions: (actions: any[] | null) => void;
  setActiveEditingIndex: (idx: number | null) => void;
  setIncludePerfilContext: (val: boolean) => void;
  setIncludeTopicoContext: (val: boolean) => void;
  setIncludeSubtopicoContext: (val: boolean) => void;
  setIncludePassosContext: (val: boolean) => void;
  setCopilotSessionHistory: (history: any[]) => void;
  setActiveSessionId: (id: string) => void;
  
  // High-level Handlers
  connectSimulatorWebSocket: (subtopicoId: string) => void;
  disconnectSimulatorWebSocket: () => void;
  sendControlCommand: (command: string, extra?: any) => void;
}

export const useEcadStore = create<EcadState>((set, get) => {
  let toastTimeout: ReturnType<typeof setTimeout> | null = null;

  return {
    streamCanvas: null,
    selectedSubtopicoId: null,
    selectedRoteiro: null,
    selectedPerfilId: null,
    selectedTopicoId: null,
    passos: [],
    loadingRoteiro: false,
    savingPassos: false,
    showPerfilModal: false,
    showTopicoModal: false,
    showSubtopicoModal: false,
    showArchivedModal: false,
    customTopicOrder: [],
    perfilForm: { id: '', nome: '', isInterno: false },
    topicoForm: { id: '', perfilId: '', titulo: '' },
    subtopicoForm: {
      id: '',
      topicoId: '',
      titulo: '',
      descricao: '',
      duracao: '2m 30s',
      dificuldade: 'Básico',
      videoUrl: '',
      pdfUrl: '',
    },
    roteiroEditForm: {
      titulo: '',
      descricao: '',
      videoUrl: '',
      pdfUrl: '',
      duracao: '',
      dificuldade: 'Básico',
      urlInicial: 'http://localhost:3443',
      velocidadeDigitacaoMs: 60,
      mouseSteps: 45,
      vozTutor: 'Kore',
    },
    savingRoteiroMeta: false,
    isEditingMetadata: false,
    activeTab: 'editor',
    snapshots: [],
    loadingSnapshots: false,
    restoringSnapshot: false,
    passosEditMode: 'visual',
    jsonValue: '[]',
    generatingPdf: false,
    pdfProgressShow: false,
    pdfProgressStep: 1,
    pdfProgressError: null,
    pdfProgressUrl: null,
    pdfLogs: [],
    showScannerModal: false,
    scannerForm: { url: 'http://localhost:3443', pageKey: 'landing_page' },
    scanningPage: false,
    isSimulating: false,
    simulationLogs: [],
    simulationSuccess: null,
    simulationModeVisible: true,
    activeStepOrdem: null,
    currentBrowserUrl: '',
    isPaused: false,
    precacheInfo: null,
    browserInstance: null,
    toastMessage: null,
    toastType: 'success',
    uiMap: {},
    isDryRunning: false,
    dryRunReport: null,
    showDryRunPanel: false,
    dryRunFullReport: null,
    retryingStep: null,
    openingStep: null,
    copilotMessages: [
      {
        role: 'assistant',
        text: 'Olá! Sou o assistente de gravação e automação do ECAD AI Studio. Posso ajudar você a criar novos roteiros de treinamento, sugerir passos de automação do Playwright para os formulários do Aluno Conectado ou validar seus seletores CSS. O que deseja fazer hoje?',
      },
    ],
    copilotInput: '',
    copilotLoading: false,
    selectedStepIndexes: [],
    proposedUiActions: null,
    activeEditingIndex: null,
    includePerfilContext: true,
    includeTopicoContext: true,
    includeSubtopicoContext: true,
    includePassosContext: true,
    copilotSessionHistory: [],
    activeSessionId: `session-${Date.now()}`,

    setStreamCanvas: (canvas) => {
      streamCanvas = canvas;
      set({ streamCanvas: canvas });
    },

    setSelectedSubtopicoId: (id, perfis) => {
      const currentId = get().selectedSubtopicoId;
      if (currentId !== id) {
        get().disconnectSimulatorWebSocket();
        set({ selectedSubtopicoId: id });
        if (id) {
          get().connectSimulatorWebSocket(id);

          // Find parent profile automatically
          if (perfis) {
            const parentPerfil = perfis.find((p) =>
              p.topicos.some((t) => t.subtopicos.some((s) => s.id === id))
            );
            if (parentPerfil) {
              set({ selectedPerfilId: parentPerfil.id });
            }
          }
        }
      }
    },

    setSelectedPerfilId: (id) => set({ selectedPerfilId: id }),
    setSelectedTopicoId: (id) => set({ selectedTopicoId: id }),
    setPassos: (passos) => {
      set({ passos });
      try {
        const json = JSON.stringify(
          passos.map((p) => ({
            ordem: p.ordem,
            tipoAcao: p.tipoAcao,
            selectorCss: p.selectorCss,
            valor: p.valor || '',
            legenda: p.legenda || '',
          })),
          null,
          2
        );
        set({ jsonValue: json });
      } catch (e) {}
    },
    setJsonValue: (jsonValue) => set({ jsonValue }),
    setActiveTab: (activeTab) => set({ activeTab }),
    setSavingPassos: (savingPassos) => set({ savingPassos }),
    setIsEditingMetadata: (isEditingMetadata) => set({ isEditingMetadata }),
    setRoteiroEditForm: (form) =>
      set((s) => ({ roteiroEditForm: typeof form === 'function' ? form(s.roteiroEditForm) : form })),
    setPerfilForm: (form) => set((s) => ({ perfilForm: typeof form === 'function' ? form(s.perfilForm) : form })),
    setTopicoForm: (form) => set((s) => ({ topicoForm: typeof form === 'function' ? form(s.topicoForm) : form })),
    setSubtopicoForm: (form) =>
      set((s) => ({ subtopicoForm: typeof form === 'function' ? form(s.subtopicoForm) : form })),
    setShowPerfilModal: (showPerfilModal) => set({ showPerfilModal }),
    setShowTopicoModal: (showTopicoModal) => set({ showTopicoModal }),
    setShowSubtopicoModal: (showSubtopicoModal) => set({ showSubtopicoModal }),
    setShowArchivedModal: (showArchivedModal) => set({ showArchivedModal }),
    setCustomTopicOrder: (customTopicOrder) => set({ customTopicOrder }),
    setPassosEditMode: (passosEditMode) => set({ passosEditMode }),
    setPdfProgressShow: (pdfProgressShow) => set({ pdfProgressShow }),
    setPdfProgressStep: (pdfProgressStep) => set({ pdfProgressStep }),
    setPdfProgressError: (pdfProgressError) => set({ pdfProgressError }),
    setPdfProgressUrl: (pdfProgressUrl) => set({ pdfProgressUrl }),
    setPdfLogs: (pdfLogs) => set({ pdfLogs }),
    setShowScannerModal: (showScannerModal) => set({ showScannerModal }),
    setScannerForm: (form) =>
      set((s) => ({ scannerForm: typeof form === 'function' ? form(s.scannerForm) : form })),
    setScanningPage: (scanningPage) => set({ scanningPage }),
    setIsSimulating: (isSimulating) => set({ isSimulating }),
    setSimulationLogs: (logs) =>
      set((s) => ({
        simulationLogs: typeof logs === 'function' ? logs(s.simulationLogs) : logs,
      })),
    setSimulationSuccess: (simulationSuccess) => set({ simulationSuccess }),
    setSimulationModeVisible: (simulationModeVisible) => set({ simulationModeVisible }),
    setActiveStepOrdem: (activeStepOrdem) => set({ activeStepOrdem }),
    setCurrentBrowserUrl: (currentBrowserUrl) => set({ currentBrowserUrl }),
    setIsPaused: (isPaused) => set({ isPaused }),
    setPrecacheInfo: (precacheInfo) => set({ precacheInfo }),
    setBrowserInstance: (browserInstance) => set({ browserInstance }),
    
    showToast: (msg, type = 'success') => {
      if (toastTimeout) clearTimeout(toastTimeout);
      set({ toastMessage: msg, toastType: type });
      toastTimeout = setTimeout(() => {
        set({ toastMessage: null });
      }, 4000);
    },
    clearToast: () => {
      if (toastTimeout) clearTimeout(toastTimeout);
      set({ toastMessage: null });
    },

    setUiMap: (uiMap) => set({ uiMap }),
    setIsDryRunning: (isDryRunning) => set({ isDryRunning }),
    setDryRunReport: (dryRunReport) => set({ dryRunReport }),
    setShowDryRunPanel: (showDryRunPanel) => set({ showDryRunPanel }),
    setDryRunFullReport: (dryRunFullReport) => set({ dryRunFullReport }),
    setRetryingStep: (retryingStep) => set({ retryingStep }),
    setOpeningStep: (openingStep) => set({ openingStep }),
    setCopilotMessages: (msgs) =>
      set((s) => ({
        copilotMessages: typeof msgs === 'function' ? msgs(s.copilotMessages) : msgs,
      })),
    setCopilotInput: (copilotInput) => set({ copilotInput }),
    setCopilotLoading: (copilotLoading) => set({ copilotLoading }),
    setSelectedStepIndexes: (indexes) =>
      set((s) => ({
        selectedStepIndexes: typeof indexes === 'function' ? indexes(s.selectedStepIndexes) : indexes,
      })),
    setProposedUiActions: (proposedUiActions) => set({ proposedUiActions }),
    setActiveEditingIndex: (activeEditingIndex) => set({ activeEditingIndex }),
    setIncludePerfilContext: (includePerfilContext) => set({ includePerfilContext }),
    setIncludeTopicoContext: (includeTopicoContext) => set({ includeTopicoContext }),
    setIncludeSubtopicoContext: (includeSubtopicoContext) => set({ includeSubtopicoContext }),
    setIncludePassosContext: (includePassosContext) => set({ includePassosContext }),
    setCopilotSessionHistory: (copilotSessionHistory) => set({ copilotSessionHistory }),
    setActiveSessionId: (activeSessionId) => set({ activeSessionId }),

    connectSimulatorWebSocket: (subtopicoId) => {
      const wsUrl = `ws://${window.location.hostname}:3335`;
      console.log(`[EcadStore] Conectando ao WebSocket do simulador: ${wsUrl}`);
      try {
        const ws = new WebSocket(wsUrl);
        activeSocket = ws;

        ws.onopen = () => {
          console.log('[EcadStore] Conectado ao stream do simulador!');
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.event === 'frame') {
              // Frame Base64 Render - draw directly on Canvas Singleton!
              const imgData = msg.data.image;
              if (streamCanvas && imgData) {
                const ctx = streamCanvas.getContext('2d');
                if (ctx) {
                  const img = new Image();
                  img.onload = () => {
                    ctx.clearRect(0, 0, streamCanvas!.width, streamCanvas!.height);
                    ctx.drawImage(img, 0, 0, streamCanvas!.width, streamCanvas!.height);
                  };
                  img.src = imgData;
                }
              }

              if (msg.data.activeStep) {
                set({ activeStepOrdem: msg.data.activeStep });
              }
              if (msg.data.url) {
                set({ currentBrowserUrl: msg.data.url });
              }
            } else if (msg.event === 'step-active') {
              set({ activeStepOrdem: msg.data.ordem });
              if (activeAudio) {
                activeAudio.pause();
                activeAudio.src = '';
              }
            } else if (msg.event === 'step-error') {
              set({ activeStepOrdem: msg.data.ordem });
              const errorScreenshot = msg.data.screenshot ? `data:image/jpeg;base64,${msg.data.screenshot}` : null;
              
              if (streamCanvas && errorScreenshot) {
                const ctx = streamCanvas.getContext('2d');
                if (ctx) {
                  const img = new Image();
                  img.onload = () => {
                    ctx.clearRect(0, 0, streamCanvas!.width, streamCanvas!.height);
                    ctx.drawImage(img, 0, 0, streamCanvas!.width, streamCanvas!.height);
                  };
                  img.src = errorScreenshot;
                }
              }

              set((s) => ({
                simulationLogs: [
                  ...s.simulationLogs,
                  `⛔ ERRO [Passo ${msg.data.ordem}] ${msg.data.tipoAcao}: ${msg.data.erro}`,
                ],
              }));

              // Error sound beep via Audio Context API
              try {
                const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
                const osc = audioCtx.createOscillator();
                const gain = audioCtx.createGain();
                osc.connect(gain);
                gain.connect(audioCtx.destination);
                osc.type = 'square';
                osc.frequency.value = 440;
                gain.gain.value = 0.15;
                osc.start();
                osc.stop(audioCtx.currentTime + 0.25);
                setTimeout(() => {
                  const osc2 = audioCtx.createOscillator();
                  const gain2 = audioCtx.createGain();
                  osc2.connect(gain2);
                  gain2.connect(audioCtx.destination);
                  osc2.type = 'square';
                  osc2.frequency.value = 330;
                  gain2.gain.value = 0.15;
                  osc2.start();
                  osc2.stop(audioCtx.currentTime + 0.35);
                }, 280);
              } catch (e) {}
            } else if (msg.event === 'status') {
              if (msg.data.browserInstance !== undefined) {
                set({ browserInstance: msg.data.browserInstance });
              }
              if (msg.data.state === 'precaching') {
                set({
                  precacheInfo: {
                    active: true,
                    total: msg.data.total || 0,
                    completed: msg.data.completed || 0,
                  },
                });
              } else if (msg.data.state === 'precaching-done') {
                set({ precacheInfo: null });
              } else if (msg.data.state === 'simulating') {
                set({ precacheInfo: null, isSimulating: true, isPaused: false });
                if (msg.data.activeStep) {
                  set({ activeStepOrdem: msg.data.activeStep });
                }
              } else if (msg.data.state === 'paused') {
                set({ isPaused: true });
                if (msg.data.activeStep) {
                  set({ activeStepOrdem: msg.data.activeStep });
                }
                if (activeAudio) {
                  activeAudio.pause();
                }
              } else if (msg.data.state === 'stopped') {
                set({ precacheInfo: null, isSimulating: false, isPaused: false, activeStepOrdem: null });
                if (activeAudio) {
                  activeAudio.pause();
                  activeAudio.src = '';
                  activeAudio = null;
                }
              }
            } else if (msg.event === 'url-change') {
              set({ currentBrowserUrl: msg.data.url });
            } else if (msg.event === 'log') {
              const logLine = msg.data || '';
              set((s) => ({
                simulationLogs: [...s.simulationLogs, logLine],
                pdfLogs: [...s.pdfLogs, logLine],
              }));

              // Auto progress logs tracking for pdf builds
              if (logLine.includes('Processando Passo')) {
                set({ pdfProgressStep: 2 });
              } else if (logLine.includes('CONSTRUINDO DOCUMENTO HTML')) {
                set({ pdfProgressStep: 3 });
              } else if (logLine.includes('RENDERIZANDO PDF A PARTIR DO HTML')) {
                set({ pdfProgressStep: 4 });
              } else if (
                logLine.includes('MANUAL EM PDF GERADO COM SUCESSO') ||
                logLine.includes('Manual PDF gerado e atualizado no banco')
              ) {
                set({ pdfProgressStep: 5 });
              }
            } else if (msg.event === 'audio') {
              if (msg.data.audio) {
                if (activeAudio) {
                  activeAudio.pause();
                  activeAudio.src = '';
                }
                const audioUrl = `data:audio/mp3;base64,${msg.data.audio}`;
                const audioObj = new Audio(audioUrl);
                activeAudio = audioObj;
                audioObj.play().catch((e) => console.error('[EcadStore] Erro ao reproduzir audio:', e));
              }
            }
          } catch (e) {
            console.error('[EcadStore] Erro ao ler mensagem do simulador:', e);
          }
        };

        ws.onclose = () => {
          console.log('[EcadStore] Desconectado do simulador');
          activeSocket = null;
        };

        ws.onerror = (err) => {
          console.error('[EcadStore] Erro no WebSocket do simulador:', err);
        };
      } catch (err) {
        console.error('[EcadStore] Falha ao criar WebSocket client:', err);
      }
    },

    disconnectSimulatorWebSocket: () => {
      if (activeSocket) {
        try {
          activeSocket.close();
        } catch (e) {}
        activeSocket = null;
      }
      if (activeAudio) {
        try {
          activeAudio.pause();
          activeAudio.src = '';
        } catch (e) {}
        activeAudio = null;
      }
    },

    sendControlCommand: (command, extra = {}) => {
      if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
        activeSocket.send(JSON.stringify({ command, ...extra }));
      }
    },
  };
});
